// サーバ権威キュー方式の op 型定義。
// クライアントが op.apply で送信し、サーバがシリアライズ後に op.commit でエコーバックする。
//
// 各 op は副作用が決定的になるよう、入力情報のみを保持する（applyOpToObjects と対応）。

import type { CanvasSizePreset } from "./canvas-bounds";
import type {
  CanvasObject,
  CommentMessage,
  PenStrokeCanvasObject,
  StrokePattern,
  TextAlign,
} from "./canvas-object";
import type { TableShapeChange } from "./table-cells";
import type {
  TextBaseStylePatch,
  TextInlineRangePatch,
  TextInlineStyleRange,
} from "./text-inline-style";

interface BaseOp {
  /** クライアント側で生成する一意 ID。op.commit / op.reject で対応付けに使う。 */
  readonly clientOpId: string;
  /** 送信時刻（ISO8601）。実体時刻の参考。 */
  readonly clientCreatedAt: string;
  /** op を生成したアクター */
  readonly actorUserId: string;
  /**
   * 操作者の表示名（日本語名）のスナップショット。サーバはこの値を、付箋の本文が実際に変わる
   * op のときだけ sticky の updatedByName へ転写し、後で他参加者が「本文の最終更新者」を
   * 即表示できるようにする（移動・リサイズ・色変更では転写しない）。署名を更新しなかった
   * 付箋向け op の echo には、更新前クライアントによる署名の再適用を防ぐためこの値を載せない。
   * 後方互換のため optional とし、旧クライアント由来の op では undefined として扱う。
   */
  readonly actorDisplayName?: string | undefined;
}

export interface CreateObjectOp extends BaseOp {
  readonly type: "object.create";
  readonly object: CanvasObject;
  /**
   * 同じユーザー操作（一括複製・削除 Undo）の中で新規作成される connector 端点の ID。
   * connector の object.create にだけ指定でき、値は from.objectId / to.objectId の部分集合
   * （1〜2 件・重複不可）に限る。
   *
   * API Gateway WebSocket の op.apply は op ごとに別 Lambda で処理されるため、送信順が
   * 「端点図形 → connector」でも connector 側の参照整合チェックが端点の永続化完了より
   * 先行し得る。サーバはこのヒントに列挙された端点の欠落に限り、上限付きバックオフで
   * 再読してから参照整合を判定する（ヒント外の欠落・自己ループ・接続不可種別は即時拒否）。
   *
   * 配送時の競合吸収にだけ使う transport metadata であり、サーバは確定後の op.commit から
   * 除去する。CanvasObject や永続化行には保存しない。クライアントの Undo / Redo 履歴では
   * 再送に必要な create op の一部として保持する。
   * 後方互換のため optional とし、旧クライアント由来の op では undefined として扱う。
   */
  readonly endpointCreateDependencies?: readonly string[] | undefined;
}

export interface DeleteObjectOp extends BaseOp {
  readonly type: "object.delete";
  readonly objectId: string;
}

export interface TransformObjectOp extends BaseOp {
  readonly type: "object.transform";
  readonly objectId: string;
  readonly transform: Partial<{
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
    readonly rotation: number;
    readonly zOrder: number;
    /** ロックの有効/無効を切り替える。true=ロック、false=解除。 */
    readonly locked: boolean;
    /** テキストの高さを利用者が明示指定したか。text 以外では適用時に無視する。 */
    readonly manualHeight: boolean;
  }>;
}

export interface UpdateTextOp extends BaseOp {
  readonly type: "object.text.update";
  readonly objectId: string;
  readonly text: string;
  /**
   * 本文変更に追従させた部分書式（正規化済み run）。`type: "text"` の対象にだけ指定できる。
   * 新クライアントは本文変更ごとに本文と run を同時送信し、サーバはこの値を権威として保存する。
   * 省略した旧クライアント由来の op では、サーバ・web が共有の単一置換差分で既存 run を rebase する
   * （＝省略を「全部消す」と解釈しない）。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * 編集元の `textStyleRevision`。`inlineStyles` を送る場合は必須で、サーバは現在値と一致しない
   * ときに op 全体を競合 reject する（古い run による上書きを防ぐ）。
   */
  readonly expectedTextStyleRevision?: number | undefined;
}

/**
 * テキスト図形の全文書式（基底）と文字範囲の部分書式を、1 op・1 commit で原子的に更新する。
 * - `baseStyle`: 全文の `color` / `fill` / `fontSize` / `bold` の部分更新。指定した項目と
 *   同名の inline override は全 run から削除する（明示操作で全文へ統一する契約）。
 * - `rangePatches`: 順序付きの範囲パッチ。各項目の `null` はその範囲の override 削除を表す。
 * `expectedText` は保存済み本文との完全一致を要求し、一致しなければ op 全体を competing reject する
 * （選択中に他者が本文を変えた場合に、別の文字へ書式が付くのを防ぐ）。
 */
export interface UpdateTextStyleOp extends BaseOp {
  readonly type: "object.text.style.update";
  readonly objectId: string;
  readonly expectedText: string;
  readonly baseStyle?: TextBaseStylePatch | undefined;
  readonly rangePatches?: readonly TextInlineRangePatch[] | undefined;
}

/**
 * 表のセル 1 つの本文を更新する。
 *
 * 既存の `object.text.update` へ相乗りしない。あれは「オブジェクト 1 つに本文 1 本」を前提とし、
 * テキスト図形専用の `textStyleRevision` による競合検知も抱えている。表はセルごとに本文があり、
 * 競合検知は既存の構造 revision とオブジェクト version で足りるため、別の op にしたほうが
 * 検証も適用も単純になる。
 */
export interface UpdateTableCellOp extends BaseOp {
  readonly type: "object.table.cell.update";
  readonly objectId: string;
  /** 行 index（0 がヘッダ行）。 */
  readonly row: number;
  /** 列 index。 */
  readonly column: number;
  /** 新しいセル本文。 */
  readonly text: string;
  /**
   * このセルの部分書式（文字範囲だけへ適用する色・背景・サイズ・太字）。
   *
   * 扱いはテキスト図形の本文更新（`object.text.update` の `inlineStyles`）と同じ契約にそろえる。
   *
   * - **配列を渡した場合は、その run を権威として保存する。空配列は「このセルの部分書式を消す」。**
   *   本文と部分書式を同じ op で確定させるためで、別々に送ると「本文は新しいのに run は古い」
   *   状態が生まれる。本文変更への追従（rebase）は送信側が行い、サーバは受け取った run を
   *   検証して保存する。
   * - **省略した場合は「このフィールドを知らない旧クライアント由来の更新」として、保存済みの
   *   run を新しい本文へ追従させる。** 省略を「全部消す」と解釈すると、部分書式を持つセルを
   *   旧タブが 1 文字編集しただけで、他の利用者が付けた書式が黙って消える。
   *
   * 確定 op（サーバからの echo）には、保存後の権威 run が必ず載る（省略した op を送った
   * 旧クライアント以外は、echo の run で同じ正規形へ収束する）。
   */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  /**
   * この op を組み立てた時点の表の構造 revision。
   *
   * row / column は位置の参照であり、行・列の挿入・削除で意味が変わる。CAS の再試行で
   * 最新状態へ再適用すると、他の参加者が行を挿入していた場合に**別のセル**を書き換える
   * （範囲内なので添字の検証も通ってしまう）。そこで、表の形が変わるたびに増える
   * revision を載せ、不一致なら適用せず op.reject にする。
   *
   * クライアントは拒否を受けて楽観適用を巻き戻し、最新の表に対して入力をやり直す。
   * 送信は表ごとに直列化されているため、自分の操作で不一致になることは無く、
   * 他の参加者が同時に行・列を増減したときだけ起きる。
   */
  readonly structureRevision: number;
  /** 確定後にサーバが載せる応答専用の版。要求時に指定しても検証で除去される。 */
  readonly resultVersion?: number | undefined;
  /** 確定後にサーバが載せる応答専用の構造 revision。要求時に指定しても検証で除去される。 */
  readonly resultStructureRevision?: number | undefined;
}

/**
 * 表の行・列を増減する。
 * 1 回の op で 1 行または 1 列だけを操作する（複数同時は扱わない）。
 * 適用に成功すると、表の `structureRevision` を 1 つ進める。
 */
export interface UpdateTableShapeOp extends BaseOp {
  readonly type: "object.table.shape.update";
  readonly objectId: string;
  /** 操作対象の表構造を固定し、再読後の別位置への適用を防ぐ。 */
  readonly structureRevision: number;
  readonly change: TableShapeChange;
  /**
   * 列の増減で `columnFractions` を持つ表の比率を更新するための新しい比率。
   *
   * **行の増減（insert-row / delete-row）では指定できず、適用側は既存の `columnFractions` を
   * そのまま保つ。** 未指定を「等分へ戻す」と解釈すると、利用者が決めた列幅が行の追加だけで
   * 失われる。列の増減では、対象の表が `columnFractions` を持つ場合に必ず指定する。
   * 持たない表（自動列幅）では指定しない（自動のままにする）。
   */
  readonly columnFractions?: readonly number[] | undefined;
  /** 確定後にサーバが載せる応答専用の版。要求時に指定しても検証で除去される。 */
  readonly resultVersion?: number | undefined;
  /** 確定後にサーバが載せる応答専用の構造 revision。要求時に指定しても検証で除去される。 */
  readonly resultStructureRevision?: number | undefined;
}

/**
 * テキスト図形・付箋の本文中にある表 1 つぶんの列幅を更新する。
 *
 * 既存の `object.style.update` へ相乗りしない。あれは「色・サイズ・寄せ」など図形全体の
 * 見た目を扱う op で、本文中の特定の表を指す概念を持たないためである。
 */
export interface UpdateTableColumnWidthsOp extends BaseOp {
  readonly type: "object.table.columns.update";
  readonly objectId: string;
  /** 本文中の表のブロック順 index。 */
  readonly tableIndex: number;
  /** 新しい列幅（em）。空配列は「保存を破棄して自動列幅へ戻す」を表す。 */
  readonly widthsEm: readonly number[];
  /** 保存時点の列数（`widthsEm.length` と一致させる。空配列のときは 0）。 */
  readonly columnCount: number;
  /**
   * 本文中の表の総数。サーバはこの件数を超える保存値を切り詰める。
   * これが無いと、本文編集で表が減ったときに余剰の保存値（末尾の非 null）を
   * 判定する材料がサーバ側に無い（サーバは本文を解析しない）。
   */
  readonly tableCount: number;
  /**
   * 保存時点のヘッダ指紋（`markdownTableHeaderKey`）。空配列のときは空文字列。
   * クライアントが計算して送り、サーバはそのまま保存する。サーバ側で本文を解析し直して
   * 再計算はしない（本文と op の順序が入れ替わると別の値になり、クライアントの楽観適用と
   * 食い違うため）。誤った値が入っても、表示側の一致確認が外れて自動列幅になるだけで、
   * 本文や他の図形を壊さない。
   */
  readonly headerKey: string;
}

export interface CommitPenStrokeOp extends BaseOp {
  readonly type: "pen.stroke.commit";
  readonly object: PenStrokeCanvasObject;
}

export interface UpdateCanvasMetaOp extends BaseOp {
  readonly type: "canvas.meta.update";
  readonly title?: string | undefined;
  readonly thumbnailKey?: string | undefined;
  /**
   * キャンバスサイズのプリセット（小 / 中 / 大）。サーバ権威で永続化し、全クライアントが同じサイズへ収束する。
   * 後方互換のため optional。未指定の op はサイズを変更しない。
   */
  readonly sizePreset?: CanvasSizePreset | undefined;
}

/**
 * 添付メディア（image / video / link-card）の参照 URL を差し替える op。
 * presigned PUT でアップロード完了した後、blob: 等のローカル URL を
 * 永続的な CloudFront 配信 URL へ更新するために使用する。
 */
export interface UpdateAttachmentSrcOp extends BaseOp {
  readonly type: "object.attachment.update";
  readonly objectId: string;
  readonly src: string;
}

/**
 * オブジェクトの見た目（色・フォントサイズ等）を更新する汎用 op。
 * - sticky / text / pen-stroke の `color`
 * - text の `fontSize`
 * - sticky / text の `textAlign`
 * - text / shape の `fill`
 * - shape の `stroke` / `strokeWidth`
 * - connector の `stroke` / `strokeWidth`
 * - pen-stroke の `thickness`
 * を 1 つの op でまとめて扱う。位置・サイズの変更は object.transform 側で扱う。
 */
export interface UpdateObjectStyleOp extends BaseOp {
  readonly type: "object.style.update";
  readonly objectId: string;
  readonly style: Partial<{
    readonly color: string;
    readonly fontSize: number;
    /** テキスト本文全体の太字。 */
    readonly bold: boolean;
    readonly fill: string;
    readonly stroke: string;
    /** 図形またはペンストロークの線種。 */
    readonly strokePattern: StrokePattern;
    /** 図形の枠線（矢印では線）またはコネクタの線の太さ（px）。 */
    readonly strokeWidth: number;
    readonly textAlign: TextAlign;
    /**
     * ペンストロークの線幅（px）。
     * 外接矩形（x / y / w / h）は点群と線幅から導出される派生値のため、適用時に再計算する。
     */
    readonly thickness: number;
  }>;
}

/**
 * コメントスレッドへメッセージ（起点コメント以降の返信）を 1 件追加する。
 * thread 作成自体は object.create を再利用するため、本 op は既存スレッドへの追記専用。
 * message の authorId / authorName はサーバが認可済みアクターで上書きする。
 * authorAvatar は接続時アバターを優先し、欠落時だけ検証済みの自己完結ラスタ data URL を採用する。
 */
export interface AddCommentMessageOp extends BaseOp {
  readonly type: "comment.message.add";
  readonly objectId: string;
  readonly message: CommentMessage;
}

/**
 * コメントスレッド内の 1 メッセージ本文を編集する。
 * サーバは「投稿者本人または管理者」のみ許可し、それ以外は op.reject する。
 */
export interface UpdateCommentMessageOp extends BaseOp {
  readonly type: "comment.message.update";
  readonly objectId: string;
  readonly messageId: string;
  readonly body: string;
}

/**
 * コメントスレッド内の 1 メッセージを削除する（ハード削除）。
 * 削除によりスレッドのメッセージが 0 件になった場合は、スレッドオブジェクトごと削除する。
 * サーバは「投稿者本人または管理者」のみ許可し、それ以外は op.reject する。
 */
export interface DeleteCommentMessageOp extends BaseOp {
  readonly type: "comment.message.delete";
  readonly objectId: string;
  readonly messageId: string;
}

export type CanvasOp =
  | CreateObjectOp
  | DeleteObjectOp
  | TransformObjectOp
  | UpdateTextOp
  | UpdateTextStyleOp
  | UpdateTableCellOp
  | UpdateTableShapeOp
  | UpdateTableColumnWidthsOp
  | CommitPenStrokeOp
  | UpdateCanvasMetaOp
  | UpdateAttachmentSrcOp
  | UpdateObjectStyleOp
  | AddCommentMessageOp
  | UpdateCommentMessageOp
  | DeleteCommentMessageOp;

// 永続化しない一時 op（broadcast のみ）
export interface TransientTransformOp {
  readonly type: "object.transient.transform";
  readonly clientOpId: string;
  readonly objectId: string;
  readonly transform: Partial<{ x: number; y: number; w: number; h: number; rotation: number }>;
}

/**
 * 複数オブジェクトの移動プレビューを 1 メッセージへまとめる一時 op。
 * 大量選択のドラッグでもメッセージ数が図形数ぶん増えないようにする。
 */
export interface TransientTransformManyOp {
  readonly type: "object.transient.transform.many";
  readonly clientOpId: string;
  readonly items: ReadonlyArray<{
    readonly objectId: string;
    readonly transform: Partial<{
      x: number;
      y: number;
      w: number;
      h: number;
      rotation: number;
    }>;
  }>;
}

/** 1 通の複数 transform プレビューへ載せる最大件数。 */
export const TRANSIENT_TRANSFORM_MANY_MAX_ITEMS = 200;

export interface TransientTextCursorOp {
  readonly type: "object.text.cursor";
  readonly clientOpId: string;
  readonly objectId: string;
  readonly cursorIndex: number;
  /**
   * 選択範囲の終端境界オフセット（本文先頭からの文字数）。範囲選択中だけ載る。
   * 範囲は半開区間 [cursorIndex, selectionEndIndex) で、cursorIndex は従来どおり
   * 選択の先頭を指す。送信側が使う選択取得の end は Range の終端境界（選択に含まれない
   * 位置）であるため、閉区間として扱うと末尾 1 文字を余分に塗ってしまう。
   * 選択が無い（collapsed）ときはこのフィールドを載せない。
   */
  readonly selectionEndIndex?: number | undefined;
  /**
   * 表のセル位置（表以外のオブジェクトでは指定しない）。
   * `cursorIndex` はそのセルの本文内のオフセットを表す。
   * `structureRevision` は送信時点の表の形を指し、受信側は形が違う表示を抑止する。
   * セル編集では選択範囲を共有しないため、`selectionEndIndex` は載せない。
   */
  readonly tableCell?:
    | { readonly row: number; readonly column: number; readonly structureRevision: number }
    | undefined;
}

export interface TransientPenAppendOp {
  readonly type: "pen.stroke.append";
  readonly clientOpId: string;
  readonly strokeId: string;
  /** 描画開始時に固定した線種。未指定の旧クライアントは実線として扱う。 */
  readonly strokePattern?: StrokePattern | undefined;
  readonly points: ReadonlyArray<{ x: number; y: number; pressure: number }>;
}

export type TransientOp =
  | TransientTransformOp
  | TransientTransformManyOp
  | TransientTextCursorOp
  | TransientPenAppendOp;
